import { useEffect, useRef, useState } from 'react';

import { type MatchingPatients, useMutateStreamPreferences, useStreamPreferences } from '../api/index.js';
import useWebSocketMessage from './websocket-hooks/useWebSocketMessage.js';
import { type ShowHideInStreamMessage, WebSocketMessageTypes } from '../models/index.js';

interface CheckedItems {
  [key: string]: boolean;
}
interface UseStreamSettingsReturn {
  isLoading: boolean;
  checkedItems: CheckedItems;
  patients: MatchingPatients[];
  onApplyStreamSettings: () => void;
  onToggle: (id: string) => void;
  onClear: () => void;
}
const useStreamSettings = (refetch: () => void): UseStreamSettingsReturn => {
  const [checkedItems, setCheckedItems] = useState<CheckedItems>({});
  const checkedItemsRef = useRef<CheckedItems>({});

  useEffect(() => {
    checkedItemsRef.current = checkedItems;
  }, [checkedItems]);

  const streamPreferences = useStreamPreferences({
    onSuccess: (data) => {
      const items: CheckedItems = {};
      data.patients.forEach((patient) => (items[patient.id] = patient.selected));
      setCheckedItems(items);
    },
  });
  const updateStreamPreferences = useMutateStreamPreferences();

  const message = useWebSocketMessage<ShowHideInStreamMessage>(WebSocketMessageTypes.SHOW_HIDE_STREAM);

  const onToggle = (id: string) => {
    setCheckedItems((prevState) => ({ ...prevState, [id]: !prevState[id] }));
  };

  const onApplyStreamSettings = async () => {
    await setStreamSettings(checkedItems);
    refetch();
  };

  const setStreamSettings = async (items: CheckedItems) => {
    try {
      const patientIds: string[] = [];
      Object.entries(items).forEach(([key, value]) => {
        if (value) {
          patientIds.push(key);
        }
      });

      await updateStreamPreferences.mutateAsync({ patientIds });
    } catch (e) {
      console.error('Error mutate async', e);
    }
  };

  const onClear = async () => {
    const items: CheckedItems = {};
    Object.keys(checkedItems).forEach((key: string) => {
      items[key] = false;
    });
    setCheckedItems(items);
    await setStreamSettings(items);
  };

  // websocket message handling
  useEffect(() => {
    if (!message) return;

    const { data: { patientId, inMonitoringHours } } = message;
    const updatedItems = { ...checkedItemsRef.current, [patientId]: inMonitoringHours };

    setCheckedItems(updatedItems);
    setStreamSettings(updatedItems).then(() => refetch());
  }, [message]);

  return {
    isLoading: streamPreferences.isLoading,
    checkedItems,
    patients: streamPreferences.data?.patients || [],
    onApplyStreamSettings,
    onToggle,
    onClear,
  };
};

export default useStreamSettings;
